import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, CreditCard, Calculator, History, Info } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { PageHeader, Badge, LoadingSpinner } from '../components/UI';
import useIsMobile from '../hooks/useIsMobile';

// ============================================================================
//  Meu painel — o que o INVESTIDOR acompanha (21/09/2026)
// ============================================================================
//  Existe para cumprir a Cláusula 10 do contrato, que promete ao investidor,
//  em tempo real: (i) o saldo utilizado de cada cartão cedido, (ii) a memória
//  de cálculo da remuneração do mês e (iii) o histórico de pagamentos.
//
//  A tela é dividida exatamente nesses três blocos, e cada um diz de onde o
//  número vem. Um investidor que não entende como se chegou ao valor não
//  confere: ele pergunta. O objetivo aqui é ele conferir sozinho.
// ============================================================================

async function api(path) {
  const r = await fetch(`${API}/remuneracao${path}`, { headers: authHeaders() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erro ${r.status}`);
  return d;
}

const mesLabel = (m) => {
  if (!/^\d{4}-\d{2}$/.test(String(m || ''))) return m || '—';
  const [a, mm] = m.split('-');
  const nomes = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
  return `${nomes[Number(mm) - 1]} de ${a}`;
};
const dataBR = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const pct = (v) => (v == null ? '—' : `${String(v).replace('.', ',')}%`);

const card = { background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16, marginBottom: 16 };
const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', color: colors.text, fontSize: 13 };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

function mesesDisponiveis(atual) {
  const lista = [];
  const [a, m] = String(atual).split('-').map(Number);
  const d = new Date(a, m - 1, 1);
  for (let i = 0; i < 12; i++) {
    lista.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return lista;
}

export default function MeuPainelPage() {
  const [data, setData] = useState(null);
  const [mes, setMes] = useState('');
  const [erro, setErro] = useState('');
  // No celular o Layout já dá a margem; o padding daqui dobrava a dele.
  const pad = useIsMobile() ? 0 : 24;

  const carregar = useCallback(async (m) => {
    try { setErro(''); setData(await api(`/meu-painel${m ? `?mes=${m}` : ''}`)); }
    catch (e) { setErro(e.message); setData(null); }
  }, []);
  useEffect(() => { carregar(mes); }, [carregar, mes]);

  if (erro) {
    return (
      <div style={{ padding: pad }}>
        <PageHeader title="Meu painel" subtitle="Acompanhamento da sua operação" />
        <div style={{ ...card, borderColor: `${colors.danger}55`, color: colors.text }}>{erro}</div>
      </div>
    );
  }
  if (!data) return <div style={{ padding: pad }}><LoadingSpinner /></div>;

  const { titular, cartoes, base_total, rendimento, data_pagamento, fechado, historico } = data;
  const pago = historico.find(h => h.competencia === data.mes && h.status === 'PAGO');

  return (
    <div style={{ padding: pad }}>
      <PageHeader title="Meu painel" subtitle={`${titular.nome || ''} · taxa de ${pct(titular.taxa_pct)} ao mês`} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: colors.textMuted }}>Competência</span>
        <select value={data.mes} onChange={e => setMes(e.target.value)}
          style={{ background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 13 }}>
          {mesesDisponiveis(data.mes).map(m => <option key={m} value={m}>{mesLabel(m)}</option>)}
        </select>
        <Badge text={pago ? 'PAGO' : (fechado ? 'FECHADO' : 'EM ANDAMENTO')}
          color={pago ? colors.success : (fechado ? colors.info : colors.warning)} />
      </div>

      {/* ── Resumo ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 16 }}>
        {[
          ['Limite comprometido', fmt(base_total), `soma dos ${cartoes.length} cartão(ões) cedido(s)`, CreditCard, colors.info],
          ['Sua remuneração', fmt(rendimento), `${pct(titular.taxa_pct)} sobre o limite comprometido`, TrendingUp, colors.success],
          ['Previsão de pagamento', dataBR(data_pagamento), 'dia 10 do mês seguinte', Calculator, colors.primary],
        ].map(([rot, valor, nota, Icone, cor]) => (
          <div key={rot} style={{ ...card, marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.textMuted, fontSize: 12 }}>
              <Icone size={14} color={cor} /> {rot}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: colors.text, marginTop: 6 }}>{valor}</div>
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2 }}>{nota}</div>
          </div>
        ))}
      </div>

      {!fechado && (
        <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'flex-start', background: `${colors.warning}10`, borderColor: `${colors.warning}44` }}>
          <Info size={16} color={colors.warning} style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.6 }}>
            <b>Esta competência ainda está aberta.</b> Os valores mudam conforme as faturas do mês fecham.
            O número vira definitivo quando a gestão fecha a competência, e o pagamento sai até o dia 10 do mês seguinte.
          </div>
        </div>
      )}

      {/* ── (i) e (ii): saldo utilizado e memória de cálculo ────────────── */}
      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calculator size={16} /> Como chegamos a esse valor
        </div>
        <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 12, lineHeight: 1.6 }}>
          O saldo utilizado de cada cartão é o total da fatura mais as parcelas futuras que já constam nela. A sua taxa
          incide sobre o limite comprometido: o uso do cartão ao fim da competência, nunca abaixo do saldo utilizado
          nem acima do limite do cartão.
        </div>
        {cartoes.length === 0 ? (
          <div style={{ fontSize: 13, color: colors.textDim }}>Nenhum cartão apurado nesta competência.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={th}>Cartão</th><th style={th}>Vencimento</th>
                <th style={{ ...th, textAlign: 'right' }}>Fatura</th>
                <th style={{ ...th, textAlign: 'right' }}>Parcelas futuras</th>
                <th style={{ ...th, textAlign: 'right' }}>Saldo utilizado</th>
                <th style={{ ...th, textAlign: 'right' }}>Limite comprometido</th>
                <th style={{ ...th, textAlign: 'right' }}>Taxa</th>
                <th style={{ ...th, textAlign: 'right' }}>Remuneração</th>
              </tr></thead>
              <tbody>
                {cartoes.map(c => (
                  <tr key={c.cartao_id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                    <td style={td}>{c.nome_cartao}
                      <div style={{ fontSize: 11, color: colors.textDim }}>{[c.banco, c.final && `final ${c.final}`].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td style={td}>{dataBR(c.data_vencimento)}</td>
                    <td style={tdNum}>{c.valor_fatura == null ? <span style={{ color: colors.textDim }}>aguardando</span> : fmt(c.valor_fatura)}</td>
                    <td style={tdNum}>{fmt(c.saldo_parcelado_futuro || 0)}</td>
                    <td style={tdNum}>{c.base_cartao == null ? '—' : fmt(c.base_cartao)}</td>
                    <td style={{ ...tdNum, fontWeight: 600 }}>{fmt(c.limite_comprometido ?? c.base_cartao ?? 0)}</td>
                    <td style={tdNum}>{pct(c.taxa_pct ?? titular.taxa_pct)}</td>
                    <td style={{ ...tdNum, color: c.aguardando_fatura ? colors.textDim : colors.success, fontWeight: 600 }}>
                      {c.aguardando_fatura ? 'aguardando fatura' : fmt(c.rendimento_cartao || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr style={{ borderTop: `2px solid ${colors.border}` }}>
                <td style={{ ...td, fontWeight: 700 }} colSpan={5}>Total</td>
                <td style={{ ...tdNum, fontWeight: 700 }}>{fmt(base_total)}</td>
                <td style={tdNum} />
                <td style={{ ...tdNum, fontWeight: 700, color: colors.success }}>{fmt(rendimento)}</td>
              </tr></tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── (iii) histórico de pagamentos ───────────────────────────────── */}
      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <History size={16} /> Histórico de pagamentos
        </div>
        {historico.length === 0 ? (
          <div style={{ fontSize: 13, color: colors.textDim }}>Nenhuma competência fechada ainda.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={th}>Competência</th><th style={th}>Cartões</th>
                <th style={{ ...th, textAlign: 'right' }}>Limite comprometido</th>
                <th style={{ ...th, textAlign: 'right' }}>Taxa</th>
                <th style={{ ...th, textAlign: 'right' }}>Remuneração</th>
                <th style={th}>Pagamento</th><th style={th}>Situação</th>
              </tr></thead>
              <tbody>{historico.map(h => (
                <tr key={h.competencia} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                  <td style={{ ...td, textTransform: 'capitalize' }}>{mesLabel(h.competencia)}</td>
                  <td style={td}>{h.qtd_cartoes}</td>
                  <td style={tdNum}>{fmt(h.base_total)}</td>
                  <td style={tdNum}>{pct(h.taxa_pct)}</td>
                  <td style={{ ...tdNum, fontWeight: 600 }}>{fmt(h.rendimento)}</td>
                  <td style={td}>{dataBR(h.data_pagamento)}{h.pago_em && <div style={{ fontSize: 11, color: colors.success }}>pago em {dataBR(h.pago_em)}</div>}</td>
                  <td style={td}><Badge text={h.status} color={h.status === 'PAGO' ? colors.success : colors.info} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ fontSize: 11, color: colors.textDim, lineHeight: 1.6 }}>
        Estes números atendem à Cláusula 10 do seu contrato: saldo utilizado de cada cartão cedido, memória de cálculo
        da remuneração do período e histórico de pagamentos. Divergência? Abra um chamado pelo menu Suporte.
      </div>
    </div>
  );
}
